import React, { useMemo, useState } from 'react';

import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';

import tenants from '~/consts/tenant';

import { salesforceHost } from '~/services/common';

import buildCustomerPortalPreviewUrl from '~/utils/buildCustomerPortalPreviewUrl';

interface Props {
  offerId: string;
  offerSlug?: string;
  size?: 'small' | 'medium' | 'large';
}

export default function OfferPagePreview({ offerId, offerSlug, size = 'medium' }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const offerPath = offerSlug ? `/offer/${offerSlug}/${offerId}` : '';
  const previewLinks = useMemo(
    () =>
      offerPath
        ? Object.values(tenants)
            .map((tenant) => {
              const href = buildCustomerPortalPreviewUrl(tenant, offerPath);

              return href ? { href, label: tenant.title } : null;
            })
            .filter((link): link is { href: string; label: string } => !!link)
        : [],
    [offerSlug, offerId],
  );

  return (
    <>
      <Button variant="outlined" size={size} startIcon={<VisibilityIcon />} onClick={() => setIsOpen(true)}>
        Preview offer page
      </Button>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Preview offer page</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            {previewLinks.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Offer preview links are unavailable until the offer slug and portal config have loaded.
              </Typography>
            )}
            {previewLinks.map((link) => (
              <Button
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                startIcon={<VisibilityIcon />}
                sx={{ justifyContent: 'flex-start' }}
              >
                {link.label}
              </Button>
            ))}
            <Button
              href={`${salesforceHost}/${offerId}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              sx={{ justifyContent: 'flex-start' }}
            >
              Salesforce
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setIsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
